/*
 * Head office and franchises (draft 11, FEATURE-MAP 13): the plan a franchise pays The Trades, where a record goes, the
 * two-step words on the security center, repeating revenue, the names switch and the cross-brand offer rule. Ported from
 * the WordPress Steward's franchise.php, scope.php, security.php, hq.php and FRANCHISE-PLAN.md.
 */
import type { Action } from "./permissions.js";
import type { Frequency, RepeatRule } from "./schedule.js";

/** The software fee a location pays The Trades each month (FRANCHISE-PLAN.md): $250, flat. */
export const FRANCHISE_FEE_CENTS = 25_000;

/** Paid add-ons with a monthly allowance. Past the allowance each use is billed at the extra price (cost plus a markup). */
export const ADDONS = ["eunice", "cold_email", "finder"] as const;
export type Addon = (typeof ADDONS)[number];

export interface AddonTerms {
  priceCents: number;
  allowance: number;
  extraCents: number;
}

/** Prices and allowances, as the owner set them (answer 101, 2026-10-28). Head office can change them per location. */
export const ADDON_DEFAULTS: Record<Addon, AddonTerms> = {
  eunice: { priceCents: 4_900, allowance: 300, extraCents: 15 },
  cold_email: { priceCents: 2_900, allowance: 500, extraCents: 2 },
  finder: { priceCents: 2_900, allowance: 1_000, extraCents: 1 },
};

export const ADDON_WORDS: Record<Addon, { name: string; unit: string }> = {
  eunice: { name: "Eunice", unit: "replies" },
  cold_email: { name: "AI cold email lines", unit: "lines" },
  finder: { name: "Lead finder", unit: "businesses found" },
};

/** One month's bill: the fee, each add-on turned on, and the extra uses past each allowance. Whole cents. */
export function franchiseBill(
  feeCents: number,
  addons: readonly { addon: Addon; on: boolean; terms: AddonTerms; used: number }[],
): { feeCents: number; addonsCents: number; extraCents: number; totalCents: number } {
  let addonsCents = 0;
  let extraCents = 0;
  for (const a of addons) {
    if (!a.on) continue;
    addonsCents += a.terms.priceCents;
    extraCents += Math.max(0, a.used - a.terms.allowance) * a.terms.extraCents;
  }
  return { feeCents, addonsCents, extraCents, totalCents: feeCents + addonsCents + extraCents };
}

/**
 * The fee for the month a franchise starts, shared by days (owner answer 102): the days from its start to the month's end
 * over the days in that month. Any other month pays the whole fee.
 */
export function firstMonthFee(feeCents: number, startedOn: string, month: string): number {
  if (startedOn.slice(0, 7) !== month) return feeCents;
  const [y, m] = month.split("-").map(Number) as [number, number];
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const used = days - Number(startedOn.slice(8, 10)) + 1;
  return Math.round((feeCents * used) / days);
}

/** Paid add-ons pause while a bill is unpaid this many days after it was made (owner answer 103). The core never locks. */
export const ADDON_PAUSE_DAYS = 30;

/** The month before a YYYY-MM-DD day, as YYYY-MM. */
export function monthBefore(day: string): string {
  const [y, m] = day.split("-").map(Number) as [number, number];
  return m === 1 ? `${String(y - 1)}-12` : `${String(y)}-${String(m - 1).padStart(2, "0")}`;
}

/** A record's ZIP: its zip field, else the last standalone 5 digits in its address (scope.php). */
export function zipOf(zip: string | null | undefined, address: string | null | undefined): string | null {
  const z = zip?.trim().slice(0, 5);
  if (z && /^\d{5}$/.test(z)) return z;
  const all = [...(address ?? "").matchAll(/(?<!\d)(\d{5})(?!\d)/g)];
  return all.at(-1)?.[1] ?? null;
}

/** ZIPs typed in a box: any spacing or commas, 5 digits each, no doubles, in order. Answers the bad ones apart. */
export function cleanZips(text: string | readonly string[]): { zips: string[]; bad: string[] } {
  const parts = (typeof text === "string" ? text.split(/[\s,;]+/) : [...text]).map((s) => s.trim()).filter(Boolean);
  const bad = parts.filter((p) => !/^\d{5}$/.test(p));
  return { zips: [...new Set(parts.filter((p) => /^\d{5}$/.test(p)))].sort(), bad };
}

/** How a record found its location: picked by hand, by its ZIP's territory, or the brand's home (no territory). */
export type PlaceHow = "picked" | "zip" | "home";

/** Two-step on the security center (security.php): on with the app, needed and not set up, or not asked for this role. */
export type TwoStepState = "app" | "not_set_up" | "not_asked";
export function twoStepState(totpEnabled: boolean, needsIt: boolean): TwoStepState {
  if (totpEnabled) return "app";
  return needsIt ? "not_set_up" : "not_asked";
}

/** The access review is due 90 days after it was last marked reviewed, and at once if it never was. */
export const REVIEW_DAYS = 90;
/** Logins that are not owners are signed out everywhere after 90 days with no use. */
export const IDLE_DAYS = 90;

/** Visits a month for repeating revenue (hq.php): weekly 52/12, every two weeks 26/12, every other month 0.5. */
export const VISITS_A_MONTH: Record<Frequency, number> = {
  "one-time": 0,
  daily: 30,
  weekly: 52 / 12,
  biweekly: 26 / 12,
  monthly: 1,
  bimonthly: 0.5,
  custom: 0,
  "as-needed": 0,
};

/** Visits a month for a custom repeat: every N days, weeks (times the days picked), months or years. */
function customVisitsAMonth(r: RepeatRule): number {
  const every = Math.max(1, r.every);
  if (r.unit === "day") return 30 / every;
  if (r.unit === "week") return (Math.max(1, r.weekdays?.length ?? 0) * 52) / 12 / every;
  if (r.unit === "month") return Math.max(1, (r.monthWeekdays?.length ? r.monthWeekdays : r.monthDays)?.length ?? 0) / every;
  return 1 / 12 / every;
}

/** A repeating job's money a month, in whole cents. */
export function monthlyCents(frequency: string, visitCents: number, rule: RepeatRule | null = null): number {
  const perMonth =
    frequency === "custom" && rule
      ? customVisitsAMonth(rule)
      : ((VISITS_A_MONTH as Partial<Record<string, number>>)[frequency] ?? 0);
  return Math.round(perMonth * visitCents);
}

/**
 * What an owner from above (HQ or the brand) may still do inside a franchise that keeps its customer names from head office.
 * Everything with a client's name in it is closed to them; the business, its people, settings, plan and security stay open.
 */
export const OPEN_WHEN_NAMES_PRIVATE: readonly Action[] = [
  "company.read",
  "company.update",
  "settings.read",
  "settings.update",
  "keys.read",
  "keys.update",
  "people.read",
  "people.invite",
  "people.update",
  "people.delete",
  "people.update_roles",
  "security.read",
  "security.update",
  "franchise.read",
];

/**
 * Cross-brand offers (owner rule): an email offer may come from any brand; a text only from the brand the client said yes
 * to texts from. A marketing opt-out at any brand stops offers from every brand (consent.php).
 */
export function offerAllowed(o: {
  channel: "email" | "sms";
  fromBrand: string;
  textConsentBrands: readonly string[];
  optedOutAnywhere: boolean;
}): boolean {
  if (o.optedOutAnywhere) return false;
  return o.channel === "email" || o.textConsentBrands.includes(o.fromBrand);
}
